import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Center,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Grid,
  HStack,
  Stack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { Button, ODCloseButton } from '@outdoorly/button';
import { TextInput } from '@outdoorly/input';
import gql from 'graphql-tag';
import { LIST_PRODUCT_FRAGMENT } from '../../pages/products';
import { Text } from '@outdoorly/text';
import { ProductGridSkeleton } from '../skeletons';
import { useQuery } from '@apollo/client';
import { makeNetworkStatus } from '../../hooks/make-network-status';
import { ProductCardWithPreview } from '../product-card-with-preview';
import * as ga from '../../lib/ga';

const PRODUCT_SEARCH = gql`
  query ProductSearch($term: String!, $first: Int!, $after: String) {
    products(
      first: $first
      after: $after
      filter: { search: $term }
      onlyVisibleOnStorefront: true
    ) {
      totalCount
      pageInfo {
        hasPreviousPage
        hasNextPage
        endCursor
        startCursor
      }
      edges {
        node {
          ...productFragment
        }
      }
    }
  }
  ${LIST_PRODUCT_FRAGMENT}
`;

export const SearchModal = ({ isOpen, onClose }) => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const searchField = React.useRef();

  const inputSize = useBreakpointValue(['2xl', '3xl', '4xl']);

  const searchTimeout = React.useRef();

  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setSearch(searchInput);
      if (searchInput && searchInput.length > 2) {
        ga.event({
          action: 'product_search',
          value: searchInput,
        });
      }
    }, 200);
    return () => {
      clearTimeout(searchTimeout.current);
    };
  }, [searchInput]);

  const { data, fetchMore, networkStatus } = useQuery(PRODUCT_SEARCH, {
    variables: { first: 20, after: null, term: search },
    notifyOnNetworkStatusChange: true,
    skip: !search,
  });

  const { loading, fetchingMore } = makeNetworkStatus(networkStatus);

  const products = data?.products.edges.map(({ node }) => node) ?? [];
  const totalProducts = data?.products.totalCount;
  const hasNextPage = data?.products.pageInfo.hasNextPage;

  return (
    <Drawer
      placement="top"
      onClose={onClose}
      isOpen={isOpen}
      size="full"
      initialFocusRef={searchField}
    >
      <DrawerOverlay>
        <DrawerContent>
          <DrawerBody>
            <Stack spacing={8} paddingY={8} paddingX={[0, 2, 4, 8]}>
              <HStack>
                <TextInput
                  placeholder="Search for a product..."
                  border="none"
                  _focus={{ border: 'none' }}
                  size={inputSize}
                  ref={searchField}
                  onChange={(event) => setSearchInput(event.target.value)}
                  minW="50%"
                  value={searchInput}
                />
                <Box>
                  <ODCloseButton size="md" onClick={onClose} mb={1} />
                </Box>
              </HStack>

              <Stack spacing={16}>
                <Text>
                  {totalProducts != null && `${totalProducts} results`}
                </Text>

                {loading && <ProductGridSkeleton />}

                <Grid
                  templateColumns={[
                    'repeat(2, minmax(0, 1fr))',
                    'repeat(2, minmax(0, 1fr))',
                    'repeat(4, minmax(0, 1fr))',
                  ]}
                  gap={4}
                >
                  {products.map((product) => (
                    <ProductCardWithPreview
                      key={product.id}
                      product={product}
                    />
                  ))}
                </Grid>
                <Box>
                  <Stack
                    spacing={5}
                    width="full"
                    maxWidth="xs"
                    mx="auto"
                    align="center"
                    mb={['70px', 0, 0]}
                  >
                    {totalProducts && (
                      <Text>{`Viewing ${products.length} of ${totalProducts}`}</Text>
                    )}
                    {hasNextPage && (
                      <Button
                        variant="solid"
                        colorScheme="red"
                        width="full"
                        onClick={() =>
                          fetchMore({
                            variables: {
                              first: 20,
                              after: data?.products.pageInfo.endCursor,
                            },
                          })
                        }
                        isLoading={fetchingMore}
                      >
                        Load More
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Stack>
            {totalProducts === 0 && (
              <Center>
                <Text
                  size="lg"
                  color="scheme.textSecondary"
                >{`No results for "${search}"`}</Text>
              </Center>
            )}
          </DrawerBody>
        </DrawerContent>
      </DrawerOverlay>
    </Drawer>
  );
};

SearchModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
};

export default SearchModal;
